/**
 * Utility functions to generate color shades from a base color
 */

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export interface HSL {
  h: number;
  s: number;
  l: number;
}

/**
 * Convert hex color to RGB
 */
export function hexToRgb(hex: string): RGB {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

/**
 * Convert RGB to HSL
 */
export function rgbToHsl(rgb: RGB): HSL {
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Convert HSL to RGB
 */
export function hslToRgb(hsl: HSL): RGB {
  const h = hsl.h / 360;
  const s = hsl.s / 100;
  const l = hsl.l / 100;

  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

/**
 * Convert RGB to hex
 */
export function rgbToHex(rgb: RGB): string {
  const toHex = (n: number) => {
    const hex = Math.round(n).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

/**
 * Generate a lighter or darker shade of a color
 */
export function adjustLightness(hex: string, lightnessAdjustment: number): string {
  const rgb = hexToRgb(hex);
  const hsl = rgbToHsl(rgb);

  // Adjust lightness
  hsl.l = Math.max(0, Math.min(100, hsl.l + lightnessAdjustment));

  const newRgb = hslToRgb(hsl);
  return rgbToHex(newRgb);
}

/**
 * Generate color shades for a base color
 * Returns shades from darkest (900) to lightest (100)
 */
export function generateColorShades(baseHex: string): Record<string, string> {
  const rgb = hexToRgb(baseHex);
  const hsl = rgbToHsl(rgb);

  // Generate shades by adjusting lightness
  // 900: darkest, 100: lightest
  const shades: Record<string, string> = {};

  // Calculate adjustments relative to base color lightness
  const baseLightness = hsl.l;

  // For shade 900 (darkest), we want around 20-30% lightness
  // For shade 100 (lightest), we want around 90-95% lightness

  shades["900"] = adjustLightness(baseHex, 25 - baseLightness);
  shades["800"] = adjustLightness(baseHex, 30 - baseLightness);
  shades["700"] = adjustLightness(baseHex, 40 - baseLightness);
  shades["500"] = adjustLightness(baseHex, 70 - baseLightness);
  shades["300"] = adjustLightness(baseHex, 85 - baseLightness);
  shades["100"] = adjustLightness(baseHex, 93 - baseLightness);

  return shades;
}

/**
 * Generate all color variables from theme colors
 */
export function generateThemeColors(theme: {
  neutral: string;
  primary: string;
  secondary: string;
}): Record<string, string> {
  const colors: Record<string, string> = {};

  // Generate shades for primary color
  const primaryShades = generateColorShades(theme.primary);
  colors["primary-blue"] = theme.primary;
  colors["primaryshade-900"] = primaryShades["900"];
  colors["primaryshade-700"] = primaryShades["700"];
  colors["primaryshade-500"] = primaryShades["500"];
  colors["primaryshade-300"] = primaryShades["300"];
  colors["primaryshade-100"] = primaryShades["100"];

  // Generate shades for secondary color
  const secondaryShades = generateColorShades(theme.secondary);
  colors["secondary-blue"] = theme.secondary;
  colors["secondaryshade-900"] = secondaryShades["900"];
  colors["secondaryshade-700"] = secondaryShades["700"];
  colors["secondaryshade-500"] = secondaryShades["500"];
  colors["secondaryshade-300"] = secondaryShades["300"];
  colors["secondaryshade-100"] = secondaryShades["100"];

  // Use neutral as base color
  colors["base"] = theme.neutral;

  // Generate gray shades from neutral
  const grayShades = generateColorShades(theme.neutral);
  colors["gray-900"] = grayShades["900"];
  colors["gray-700"] = grayShades["700"];
  colors["gray-500"] = grayShades["500"];
  colors["gray-200"] = adjustLightness(theme.neutral, 85 - rgbToHsl(hexToRgb(theme.neutral)).l);
  colors["gray-100"] = adjustLightness(theme.neutral, 93 - rgbToHsl(hexToRgb(theme.neutral)).l);

  // Muted and subtle colors derived from neutral
  colors["muted"] = adjustLightness(theme.neutral, 20);
  colors["subtle"] = adjustLightness(theme.neutral, 50);

  return colors;
}
